import { EnabledAction, Field, ScopeOption } from "../../../../../../models/CommonModel";
import { ModelWithSortedFields } from "../../CommonModelUtils";

/**
 * Pure mappings between a Common Model's stored state and the three-way ScopeOption,
 * plus the API update payload for a scope change.
 */

export interface ScopeUpdateArgs {
  modelName: string;
  fieldName?: string;
  enabledActions: EnabledAction[];
  optionalActions: EnabledAction[];
  nonOptionalActions: EnabledAction[];
  isFieldConfigurable?: boolean;
}

// Derive the current scope of an object capability (Read/Write) from model state.
export const scopeForModelCapability = (
  model: ModelWithSortedFields,
  capability: EnabledAction,
  areOptionalScopesEnabled: boolean,
): ScopeOption => {
  const isConfigurable = model.end_user_configurable_actions.includes(capability);
  const isEnabled = model.enabled_model_actions.includes(capability);
  if (isConfigurable && areOptionalScopesEnabled) return ScopeOption.OPTIONAL;
  return isEnabled ? ScopeOption.ENABLED : ScopeOption.DISABLED;
};

// Derive the current scope of a field from field state.
export const scopeForField = (field: Field, areOptionalScopesEnabled: boolean): ScopeOption => {
  if (field.is_end_user_configurable && areOptionalScopesEnabled) return ScopeOption.OPTIONAL;
  return field.is_enabled ? ScopeOption.ENABLED : ScopeOption.DISABLED;
};

// Apply a scope choice onto a model capability, returning a new model (optimistic render state).
export const applyModelCapabilityScope = (
  model: ModelWithSortedFields,
  capability: EnabledAction,
  scope: ScopeOption,
): ModelWithSortedFields => {
  const enabled = new Set(model.enabled_model_actions);
  const configurable = new Set(model.end_user_configurable_actions);
  if (scope === ScopeOption.DISABLED) {
    enabled.delete(capability);
    configurable.delete(capability);
  } else {
    enabled.add(capability);
    if (scope === ScopeOption.OPTIONAL) configurable.add(capability);
    else configurable.delete(capability);
  }
  return {
    ...model,
    enabled_model_actions: [...enabled],
    end_user_configurable_actions: [...configurable],
  };
};

// Apply a scope choice onto a field, returning a new field (optimistic render state).
export const applyFieldScope = (field: Field, scope: ScopeOption): Field => ({
  ...field,
  is_enabled: scope !== ScopeOption.DISABLED,
  is_end_user_configurable: scope === ScopeOption.OPTIONAL,
});

// Build the API payload for an object capability change. `model` must reflect the latest
// state so the untouched capability is preserved.
export const buildModelCapabilityUpdate = (
  model: ModelWithSortedFields,
  capability: EnabledAction,
  scope: ScopeOption,
): ScopeUpdateArgs => {
  const isOptional = scope === ScopeOption.OPTIONAL;
  const isEnabling = scope === ScopeOption.ENABLED || isOptional;
  const enabledActions: EnabledAction[] = isEnabling ? [capability] : [];
  if (
    capability === EnabledAction.READ &&
    model.enabled_model_actions.includes(EnabledAction.WRITE)
  ) {
    enabledActions.push(EnabledAction.WRITE);
  }
  if (
    capability === EnabledAction.WRITE &&
    model.enabled_model_actions.includes(EnabledAction.READ)
  ) {
    enabledActions.push(EnabledAction.READ);
  }
  return {
    modelName: model.name,
    enabledActions,
    optionalActions: isOptional ? [capability] : [],
    nonOptionalActions: !isEnabling || (isEnabling && !isOptional) ? [capability] : [],
  };
};

// Build the API payload for a field change (fields toggle only the Read action).
export const buildFieldUpdate = (
  modelName: string,
  fieldName: string,
  scope: ScopeOption,
): ScopeUpdateArgs => {
  const isOptional = scope === ScopeOption.OPTIONAL;
  const isEnabled = scope === ScopeOption.ENABLED || isOptional;
  return {
    modelName,
    fieldName,
    enabledActions: isOptional || isEnabled ? [EnabledAction.READ] : [],
    optionalActions: isOptional ? [EnabledAction.READ] : [],
    nonOptionalActions: !isEnabled || (isEnabled && !isOptional) ? [EnabledAction.READ] : [],
    isFieldConfigurable: isOptional,
  };
};
